import { Directive, ElementRef, forwardRef, HostListener, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export function formatMoneyInput(value: string): string {
  const cleaned = value.replace(/[^0-9,.-]/g, '').replace(/\./g, '');
  const negative = cleaned.startsWith('-') ? '-' : '';
  const [whole, ...decimals] = cleaned.replace(/-/g, '').split(',');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return negative + grouped + (decimals.length ? ',' + decimals.join('') : '');
}

export function parseMoneyInput(value: string): number | null {
  const normalized = value.replace(/\./g, '').replace(',', '.');
  if (!normalized || normalized === '-') return null;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

@Directive({
  selector: 'input[appMoneyInput]',
  standalone: true,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MoneyInputDirective), multi: true }],
})
export class MoneyInputDirective implements ControlValueAccessor {
  private readonly input = inject<ElementRef<HTMLInputElement>>(ElementRef).nativeElement;
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: number | string | null): void {
    this.input.value = value == null || value === '' ? '' : formatMoneyInput(String(value).replace('.', ','));
  }
  registerOnChange(fn: (value: number | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(disabled: boolean): void { this.input.disabled = disabled; }

  @HostListener('input')
  handleInput(): void {
    const raw = this.input.value;
    const caret = this.input.selectionStart ?? raw.length;
    const significant = raw.slice(0, caret).replace(/\./g, '').length;
    const formatted = formatMoneyInput(raw);
    this.input.value = formatted;
    let position = 0;
    let count = 0;
    while (position < formatted.length && count < significant) {
      if (formatted[position] !== '.') count++;
      position++;
    }
    this.input.setSelectionRange(position, position);
    this.onChange(parseMoneyInput(formatted));
  }

  @HostListener('blur')
  handleBlur(): void { this.onTouched(); }
}
